import React, { useMemo, useState, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import './ProjectTable.css';
import { useNavigate } from 'react-router-dom';
import arrowIcon from '../assets/download.png';


function ProjectTable({ data }) {
  const [filter, setFilter] = useState('');
  const [sorting, setSorting] = useState([{ id: 'Decision', desc: false }]);
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      { accessorKey: 'ProjectReference', header: 'Project Reference' },
      { accessorKey: 'ProjectName', header: 'Project Name' },
      { accessorKey: 'ApplicantName', header: 'Applicant' },
      { accessorKey: 'ApplicationCategory', header: 'Category' },
      { accessorKey: 'Region', header: 'Region' },
      { accessorKey: 'ProjectStatus', header: 'Status' },
      { accessorKey: 'Decision', header: 'Decision' },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    state: { 
      globalFilter: filter,
      sorting,
    },
    onGlobalFilterChange: setFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleDownload = useCallback(() => {
    const headers = columns.map((c) => c.header).join(',');
    const rows = table
      .getRowModel()
      .rows.map((row) =>
        columns
          .map((col) => {
            const raw = row.original[col.accessorKey] ?? '';
            return `"${String(raw).replace(/"/g, '""')}"`;
          })
          .join(',')
      )
      .join('\n');

    const csv = [headers, rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'projects.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [columns, table]);

  return (
    <div className="project-table-wrapper">
      <div className="table-header">
        <h2>All Projects</h2>
        <button className="download-btn" onClick={handleDownload}>
          Download
          <img src={arrowIcon} alt="" className="arrow-icon" />
        </button>
      </div>

      <div className="project-table-table">
        <input
          type="text"
          placeholder="Search for a project..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="table-filter-input"
        />

        <div className="project-table-scroll">
          <table className="project-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(
                (row) =>
                  row.original.ProjectReference !== '' && (
                    <tr
                      key={row.id}
                      onClick={() =>
                        navigate(`/project/${row.original.ProjectReference}`, {
                          state: row.original,
                        })
                      }
                      className="clickable-row"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ProjectTable;
